import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, RefreshCw, UserCheck, AlertTriangle, Trash2, CheckSquare, Square, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin / Supervisor (Admin)" },
  { value: "supervisor", label: "Admin / Supervisor (Supervisor)" },
  { value: "runner", label: "Yard Runner" },
  { value: "crew_leader", label: "Crew Leader" },
  { value: "viewer", label: "Regular Crew (read-only)" },
];

const ROLE_PILL = {
  admin: "bg-rose-100 text-rose-800 border-rose-300",
  supervisor: "bg-orange-100 text-orange-800 border-orange-300",
  runner: "bg-amber-100 text-amber-800 border-amber-300",
  crew_leader: "bg-indigo-100 text-indigo-800 border-indigo-300",
  viewer: "bg-slate-100 text-slate-700 border-slate-300",
};

const PROTECTED_EMAILS = new Set([
  "chris.alderson.ca@gmail.com",
  "admin@rougee.com",
]);

export default function AdminRoles() {
  const { isPrivileged, user, userLocation, isGlobalAdmin } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [locFilter, setLocFilter] = useState("");
  const [locOptions, setLocOptions] = useState([]);
  // Create-user panel state
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "crew_leader", location: "" });
  const [creating, setCreating] = useState(false);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [savingSupId, setSavingSupId] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (isGlobalAdmin && locFilter) params.location = locFilter;
      const { data } = await api.get("/auth/users", { params });
      setUsers(data);
      setSelected(new Set());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPrivileged) {
      nav("/", { replace: true });
      return;
    }
    load();
    // Fetch the list of allowed locations (for the create panel + global-admin filter).
    (async () => {
      try {
        const { data } = await api.get("/locations");
        const opts = (data?.locations || []).filter(Boolean);
        setLocOptions(opts);
        if (opts.length && !newUser.location) {
          setNewUser((s) => ({ ...s, location: userLocation || opts[0] }));
        }
      } catch {
        /* non-fatal */
      }
    })();
    // Fetch active supervisor names for inline assignment.
    (async () => {
      try {
        const { data } = await api.get("/app-config/supervisor");
        setSupervisorOptions(
          (data?.rows || []).filter((r) => !r.archived).map((r) => r.label)
        );
      } catch {
        /* non-fatal */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged, locFilter]);

  const changeRole = async (target, newRole) => {
    if (target.email === user?.email) {
      toast.error("You cannot change your own role from here");
      return;
    }
    setSavingId(target.email);
    try {
      await api.post("/auth/promote", {
        email: target.email,
        role: newRole,
        grant: true,
      });
      toast.success(`${target.email} → ${newRole}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingId("");
    }
  };

  const setSupervisor = async (target, supName) => {
    setSavingSupId(target.email);
    try {
      await api.post("/auth/users/supervisor", {
        email: target.email,
        supervisor_name: supName || null,
      });
      toast.success(`${target.email} → ${supName || "no supervisor"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingSupId("");
    }
  };

  const createUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || newUser.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/auth/users", {
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password,
        role: newUser.role,
        location: newUser.location || undefined,
      });
      toast.success(`Created ${newUser.email}`);
      setNewUser({ name: "", email: "", password: "", role: "crew_leader", location: userLocation || (locOptions[0] || "") });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  // Selection helpers
  const isProtected = (u) => {
    const em = (u.email || "").toLowerCase();
    return em === (user?.email || "").toLowerCase() || PROTECTED_EMAILS.has(em);
  };
  const selectableUsers = useMemo(() => users.filter((u) => !isProtected(u)), [users, user]);
  const allSelected = selectableUsers.length > 0 && selected.size === selectableUsers.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (email) => {
    const em = (email || "").toLowerCase();
    if (PROTECTED_EMAILS.has(em) || em === (user?.email || "").toLowerCase()) return;
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableUsers.map((u) => u.email)));
  };

  if (!isPrivileged) return null;

  const selectedList = Array.from(selected);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Admin / Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
            User Roles
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Assign permissions for Yard Runner, Crew Leader, Regular Crew, and Admin / Supervisor.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isGlobalAdmin && locOptions.length > 0 && (
            <select
              value={locFilter}
              onChange={(e) => setLocFilter(e.target.value)}
              data-testid="admin-roles-location-filter"
              className="rg-tap px-3 border-2 border-slate-300 rounded-sm bg-white font-bold uppercase tracking-wide text-xs text-slate-700"
            >
              <option value="">All locations</option>
              {locOptions.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            data-testid="admin-roles-create-toggle"
            className="rg-tap px-4 border-2 border-emerald-600 hover:border-emerald-800 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-white bg-emerald-700 hover:bg-emerald-800 text-xs"
          >
            {createOpen ? "Close" : "+ Create user"}
          </button>
          <a
            href="/admin/deletions"
            data-testid="admin-deletions-link"
            className="rg-tap px-3 border-2 border-rose-300 hover:border-rose-600 rounded-sm flex items-center gap-1.5 font-bold uppercase tracking-wide text-xs text-rose-700 bg-white"
          >
            Recent Deletions
          </a>
          <button
            onClick={load}
            data-testid="admin-roles-refresh"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {createOpen && (
        <div
          className="rg-card p-4 mb-4 border-2 border-emerald-300 bg-emerald-50"
          data-testid="admin-roles-create-panel"
        >
          <div className="font-bold uppercase tracking-wider text-xs text-emerald-800 mb-2">
            Create new user
            {!isGlobalAdmin && (
              <span className="ml-2 text-[10px] text-slate-600 normal-case">
                New users are created in your location: <strong>{userLocation}</strong>
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="Full name"
              data-testid="admin-create-name"
              className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            />
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="email@company.com"
              data-testid="admin-create-email"
              className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            />
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="Password (6+ chars)"
              minLength={6}
              data-testid="admin-create-password"
              className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              data-testid="admin-create-role"
              className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isGlobalAdmin ? (
              <select
                value={newUser.location}
                onChange={(e) => setNewUser({ ...newUser, location: e.target.value })}
                data-testid="admin-create-location"
                className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-slate-900 outline-none"
              >
                {locOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            ) : (
              <input
                value={userLocation}
                disabled
                data-testid="admin-create-location-locked"
                className="rg-tap border-2 border-slate-200 rounded-sm px-3 text-sm bg-slate-100 text-slate-500"
              />
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={createUser}
              disabled={creating}
              data-testid="admin-create-submit"
              className="rg-tap px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white border-2 border-emerald-900 rounded-sm font-bold uppercase tracking-wider text-xs flex items-center gap-2 disabled:opacity-60"
            >
              {creating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create user
            </button>
          </div>
        </div>
      )}

      <div className="rg-card p-4 mb-4 bg-amber-50 border-2 border-amber-300 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <div className="text-sm text-amber-900">
          <strong>Permissions reference:</strong> Admin / Supervisor = full access &amp; delete history.
          Yard Runner = full shipment + workflow access (no history deletion). Crew Leader = create
          requests + edit only their assigned records. Regular Crew = read-only.
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="bulk-users-bar"
          className="rg-card p-3 mb-4 bg-slate-900 text-white border-2 border-slate-900 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="text-sm font-bold uppercase tracking-wider">
            <span data-testid="bulk-users-selected-count">{selected.size}</span>
            {selected.size === 1 ? " user selected" : " users selected"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              data-testid="bulk-users-clear"
              className="rg-tap px-3 border-2 border-slate-600 hover:border-white rounded-sm bg-slate-800 hover:bg-slate-700 flex items-center gap-1.5 font-bold uppercase tracking-wide text-xs"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              data-testid="bulk-users-delete-btn"
              className="rg-tap px-3 border-2 border-rose-500 hover:border-white rounded-sm bg-rose-600 hover:bg-rose-700 flex items-center gap-1.5 font-bold uppercase tracking-wide text-xs"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete selected
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="rg-card p-3 mb-4 border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500">Loading users…</div>
      ) : (
        <div className="rg-card overflow-hidden" data-testid="admin-roles-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b-2 border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-700">
                <th className="py-2.5 px-3 w-10">
                  <button
                    type="button"
                    onClick={toggleAll}
                    disabled={selectableUsers.length === 0}
                    data-testid="bulk-users-select-all"
                    aria-label={allSelected ? "Clear selection" : "Select all"}
                    className="rg-tap inline-flex items-center justify-center h-6 w-6 hover:bg-slate-200 rounded-sm disabled:opacity-50"
                  >
                    {allSelected || someSelected ? (
                      <CheckSquare className="h-4 w-4 text-slate-700" />
                    ) : (
                      <Square className="h-4 w-4 text-slate-500" />
                    )}
                  </button>
                </th>
                <th className="py-2.5 px-4">Name</th>
                <th className="py-2.5 px-4">Email</th>
                <th className="py-2.5 px-4">Location</th>
                <th className="py-2.5 px-4">Supervisor Name</th>
                <th className="py-2.5 px-4">Current role</th>
                <th className="py-2.5 px-4">Change role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const protectedAcct = isProtected(u);
                const checked = selected.has(u.email);
                return (
                  <tr
                    key={u.email}
                    className={`border-b border-slate-100 ${checked ? "bg-rose-50" : ""}`}
                    data-testid={`admin-roles-row-${u.email}`}
                  >
                    <td className="py-2.5 px-3 w-10">
                      <button
                        type="button"
                        onClick={() => toggleOne(u.email)}
                        disabled={protectedAcct}
                        data-testid={`bulk-users-select-${u.email}`}
                        aria-label={protectedAcct ? "Protected account" : (checked ? "Deselect" : "Select")}
                        className="rg-tap inline-flex items-center justify-center h-6 w-6 hover:bg-slate-100 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed"
                        title={protectedAcct ? "Protected account — cannot be deleted" : ""}
                      >
                        {checked ? (
                          <CheckSquare className="h-4 w-4 text-rose-700" />
                        ) : (
                          <Square className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </td>
                    <td className="py-2.5 px-4 font-bold text-slate-900">{u.name}</td>
                    <td className="py-2.5 px-4 text-slate-700">{u.email}</td>
                    <td className="py-2.5 px-4">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border-2 border-slate-300 bg-slate-50 text-slate-700"
                        data-testid={`admin-roles-location-${u.email}`}
                      >
                        {u.location || "—"}
                        {u.is_global_admin && (
                          <span className="ml-0.5 bg-rose-600 text-white px-1 py-0 text-[8px] font-bold uppercase tracking-wider rounded-sm">
                            G
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <select
                        value={u.Supervisor_Name || ""}
                        disabled={savingSupId === u.email}
                        onChange={(e) => setSupervisor(u, e.target.value)}
                        data-testid={`admin-roles-supervisor-${u.email}`}
                        className="rg-tap border-2 border-slate-300 rounded-sm px-2 py-1 text-sm bg-white focus:border-slate-900 outline-none max-w-[180px]"
                      >
                        <option value="">— None —</option>
                        {supervisorOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        {/* If the user already has a name not in the active list, still show it */}
                        {u.Supervisor_Name && !supervisorOptions.includes(u.Supervisor_Name) && (
                          <option value={u.Supervisor_Name}>{u.Supervisor_Name} (archived)</option>
                        )}
                      </select>
                    </td>
                    <td className="py-2.5 px-4">
                      <span
                        className={`inline-block border-2 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          ROLE_PILL[u.role] || "bg-slate-100 border-slate-300"
                        }`}
                        data-testid={`admin-roles-current-${u.email}`}
                      >
                        {u.role_label || u.role}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      {u.email === user?.email ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <UserCheck className="h-3.5 w-3.5" />
                          That's you
                        </span>
                      ) : (
                        <select
                          value={u.role || "crew_leader"}
                          disabled={savingId === u.email}
                          onChange={(e) => changeRole(u, e.target.value)}
                          data-testid={`admin-roles-select-${u.email}`}
                          className="rg-tap border-2 border-slate-300 rounded-sm px-2 py-1 text-sm bg-white focus:border-slate-900 outline-none"
                        >
                          {ROLE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BulkDeleteUsersDialog
        open={confirmOpen}
        emails={selectedList}
        onClose={() => setConfirmOpen(false)}
        onDeleted={() => {
          setConfirmOpen(false);
          load();
        }}
      />
    </div>
  );
}

function BulkDeleteUsersDialog({ open, emails, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReason("");
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const ready = confirmText.trim().toUpperCase() === "DELETE" && !busy && emails.length > 0;

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/users/delete", {
        emails,
        reason: reason.trim() || undefined,
        confirm: "DELETE",
      });
      toast.success(`Deleted ${data.deleted} user${data.deleted === 1 ? "" : "s"}`);
      if ((data.skipped || []).length) {
        toast(`${data.skipped.length} skipped (protected / not found)`, { duration: 5000 });
      }
      onDeleted();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={busy ? undefined : onClose}
      data-testid="bulk-users-delete-overlay"
    >
      <div
        className="bg-white rounded-sm border-2 border-rose-700 shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-users-delete-modal"
      >
        <header className="px-5 py-4 border-b-2 border-rose-200 bg-rose-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trash2 strokeWidth={2.5} className="h-5 w-5 text-rose-700" />
            <h2 className="font-display text-lg uppercase tracking-tight text-rose-900">
              Delete {emails.length} user{emails.length === 1 ? "" : "s"}?
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="bulk-users-delete-close"
            className="rg-tap p-1.5 rounded-sm hover:bg-rose-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            This will <strong className="text-rose-700">permanently delete</strong> the following user
            account{emails.length === 1 ? "" : "s"}. Deletions are audit-logged but cannot be reversed.
          </p>
          <div
            className="max-h-40 overflow-auto bg-slate-50 border-2 border-slate-200 rounded-sm p-2 font-mono text-xs"
            data-testid="bulk-users-delete-list"
          >
            {emails.map((em) => (
              <div key={em} className="py-0.5 text-slate-800">{em}</div>
            ))}
          </div>

          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">
              Reason (optional)
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              placeholder="e.g. Left the company, duplicate account…"
              data-testid="bulk-users-delete-reason"
              className="rg-tap mt-1 w-full min-h-[40px] px-3 border-2 border-slate-300 focus:border-slate-900 rounded-sm bg-white text-sm"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-rose-700">
              Type DELETE to confirm
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={busy}
              autoFocus
              placeholder="DELETE"
              data-testid="bulk-users-delete-confirm"
              className="rg-tap mt-1 w-full min-h-[40px] px-3 border-2 border-rose-300 focus:border-rose-700 rounded-sm bg-white text-sm font-mono"
            />
          </label>

          {err && (
            <div
              className="text-sm font-bold text-rose-700 bg-rose-50 border-2 border-rose-200 px-3 py-2 rounded-sm"
              data-testid="bulk-users-delete-error"
            >
              {err}
            </div>
          )}
        </div>

        <footer className="px-5 py-4 border-t-2 border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="bulk-users-delete-cancel"
            className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white font-bold uppercase tracking-wide text-sm text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            data-testid="bulk-users-delete-submit"
            className="rg-tap px-4 border-2 border-rose-700 hover:border-rose-900 rounded-sm bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase tracking-wide text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Deleting…" : `Delete ${emails.length}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
